import React from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ReportSelector from "../../components/ReportSelector";
import * as layouts from "../../components/ReportLayouts/index";
import moment from "moment-timezone";
import "./NotoSansGujarati";
import price from "../../helpers/calculatePrice";
import { useSelector } from "react-redux";

const ReportCreator = ({
  role,
  reportData,
  initialEffectFunction,
  getData,
  reportInfo,
  optionData,
}) => {
  const title = reportInfo?.title;
  const selectorData = reportInfo?.selectorFormData;
  const noPadding = reportInfo?.noPadding;

  const allTables = useSelector((state) => state.branch.tables);
  const reportLayouts = reportInfo?.layouts;

  const [selectedParams, setSelectedParams] = React.useState();

  const downloadPdf = async () => {
    try {
      const data = reportData.table || [];

      const doc = new jsPDF("landscape");
      doc.setFont("NotoSansGujarati-Regular", "normal");
      doc.setFontSize(10);

      const tableData = [];

      let summaryIndex = 0;
      let currentDateKey = null;

      const grandTotals = (reportData?.lastMonthsOrder || []).reduce(
        (acc, cur) => {
          acc.qty += cur.totalItemQuantity || 0;
          acc.cgst += cur.totalCGST || 0;
          acc.sgst += cur.totalSGST || 0;
          acc.total += cur.totalGrandTotal || 0;
          return acc;
        },
        { qty: 0, cgst: 0, sgst: 0, total: 0 }
      );

      data.forEach((item, index) => {
        const orderDate = moment.tz(item.createdAt, "Asia/Kolkata");

        const day = orderDate.date();
        const month = orderDate.month() + 1;
        const year = orderDate.year();

        const dateKey = `${year}-${month}-${day}`;

        // If date changes → push previous summary
        if (reportData?.lastMonthsOrder && reportData.lastMonthsOrder.length > 0 && currentDateKey && currentDateKey !== dateKey) {
          const summary = reportData.lastMonthsOrder[summaryIndex];

          tableData.push([
            `TOTAL (${summary._id.day}-${summary._id.month}-${summary._id.year})`,
            '',
            '',
            summary.totalItemQuantity,
            summary.totalGrandTotal - (summary.totalCGST + summary.totalSGST),
            summary.totalSGST,
            summary.totalCGST,
            '',
            summary.totalCGST + summary.totalSGST,
            '',
            '',
            summary.totalGrandTotal
          ]);

          summaryIndex++; // move to next day summary
        }

        currentDateKey = dateKey;

        // Calculate order quantity
        let totalQuantity = 0;

        let tableType = allTables.filter((table) => { return table?.tableNumber === item?.tableNumber });

        // Share order-level discount across items proportionally to each item's gross (quantity * price)
        let orderDiscount = (item?.otherCharges || 0) - (item?.discount || 0); // absolute discount amount for the whole order
        let itemGrossList = item.orderItems.map((oi) => {
          return ((oi.quantity * price(tableType?.tableType, oi)) || oi.itemTotal || 0);
        });
        let totalGross = itemGrossList.reduce((s, v) => s + v, 0) || 0;

        let items = item.orderItems.map((oi, idx) => {
          let quantity = oi.quantity || 0;
          totalQuantity += quantity;

          const itemGross = itemGrossList[idx] || 0;
          // proportion of the order discount this item should take
          const discountShare = totalGross > 0 ? (itemGross / totalGross) * orderDiscount : 0;
          // deduct discount share from the item's gross price
          const discountedItemPrise = Math.max(0, itemGross + discountShare);

          // recompute GST amounts based on discounted item price
          const cgstRate = oi.cgst || 0;
          const gstPerComponent = (cgstRate * discountedItemPrise) / 100;
          const sgstAmount = gstPerComponent;
          const cgstAmount = gstPerComponent;
          const totalGst = sgstAmount + cgstAmount;
          const taxable = Math.max(0, discountedItemPrise - totalGst);

          return [
            '',
            '',
            oi.itemName,
            quantity,
            taxable.toFixed(2),
            sgstAmount.toFixed(2),
            cgstAmount.toFixed(2),
            '',
            totalGst.toFixed(2),
            '',
            '',
            discountedItemPrise.toFixed(2)
          ];
        });

        // Push order row
        tableData.push([
          moment(item.createdAt).format("DD-MM-YYYY"),
          item.branchOrderNumber,
          '',
          totalQuantity,
          (item.grandTotal - (item.cgstCharges + item.sgstCharges)).toFixed(2),
          (item.sgstCharges).toFixed(2),
          (item.cgstCharges).toFixed(2),
          '',
          (item.cgstCharges + item.sgstCharges).toFixed(2),
          (item.discount).toFixed(2),
          (item.otherCharges).toFixed(2),
          (item.grandTotal).toFixed(2),
        ]);

        // Push item rows
        tableData.push(...items);

        // If last order → push final summary
        if (reportData?.lastMonthsOrder && reportData.lastMonthsOrder.length > 0 && index === data.length - 1) {
          const summary = reportData.lastMonthsOrder[summaryIndex];

          tableData.push([
            `TOTAL (${moment(item.createdAt).format("DD-MM-YYYY")})`,
            '',
            '',
            summary.totalItemQuantity ?? 0,
            summary.totalGrandTotal - (summary.totalCGST + summary.totalSGST) ?? 0,
            summary.totalSGST ?? 0,
            summary.totalCGST ?? 0,
            '',
            summary.totalCGST + summary.totalSGST ?? 0,
            '',
            '',
            summary.totalGrandTotal ?? 0
          ]);
        }

      });

      tableData.push([
        'GRAND TOTAL',
        '',
        '',
        grandTotals.qty,
        grandTotals.total - (grandTotals.cgst + grandTotals.sgst),
        grandTotals.sgst,
        grandTotals.cgst,
        '',
        grandTotals.cgst + grandTotals.sgst,
        '',
        '',
        grandTotals.total
      ]);

      doc.text("Order GST Report", 14, 10);

      doc.autoTable({
        head: [[
          "Date",
          "Bill Number",
          "Item",
          "Quantity",
          "Taxable",
          "SGST",
          "CGST",
          "IGST",
          "Total GST",
          "Discount",
          "Other Charges",
          "Amount",
        ]],
        body: tableData,
        startY: 20,
        styles: { fontSize: 8, font: "NotoSansGujarati-Regular" },
        didParseCell: function (data) {
          // Check if row is TOTAL row
          if (data.row.raw[0] && String(data.row.raw[0]).includes('TOTAL')) {
            data.cell.styles.fillColor = [220, 230, 241]; // light blue
            data.cell.styles.textColor = [0, 0, 0];       // black text
            data.cell.styles.fontStyle = 'bold';
          }

          if (data.row.raw[0] === 'GRAND TOTAL') {
            data.cell.styles.fillColor = [180, 198, 231];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      doc.save(`OrderGSTReport.pdf`);
      console.log("saved")
    } catch (err) {
      console.error("downloadPdf error", err);
    }
  };

  return (
    <div className="page-content-tab">
      {title && (
        // <div className="card ">
        //   <div className="card-header bg-primary">
        //     <h4 className="text-white">{title}</h4>
        //   </div>
        // </div>
        <h4 className="text-primary mb-4">{title}</h4>
      )}
      {selectorData && (
        <ReportSelector
          getReportData={(data) => {
            getData(data);
            setSelectedParams(data);
          }}
          formData={selectorData}
          initialEffectFunction={initialEffectFunction}
          optionData={optionData}
          noPadding={noPadding}
        />
      )}
      {(title === "Order Report") && (
        <div style={{ marginBottom: 12 }}>
          {/* <button className="btn btn-success mr-2" onClick={downloadExcel}>
            Export Excel
          </button> */}
          <button className="btn btn-primary" onClick={downloadPdf}>
            Export PDF
          </button>
        </div>
      )}
      {reportLayouts && (
        <div className="row">
          {reportLayouts.map((lay, index) => {
            const width = lay.width || 12;
            const Component = layouts[lay.type];
            return (
              <div className={`col-lg-${width}`}>
                {Component ? (
                  <Component
                    {...lay}
                    data={reportData[lay.dataVariable] || []}
                    headerVariable={reportData[lay.headerVariable]}
                    selectedParams={selectedParams}
                  />
                ) : (
                  <div>no Layout Found</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportCreator;
